export function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatLocalTime(date) {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

export function formatDateTimeLabel(date) {
  return `${formatLocalDate(date)} ${formatLocalTime(date)}`;
}

export function normalizeClockValue(clock) {
  const [hRaw, mRaw] = String(clock || '').split(':');
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatTimeFromMinutes(totalMinutes) {
  const safeMinutes = Math.max(0, Math.min(24 * 60, Number(totalMinutes) || 0));
  const h = Math.floor(safeMinutes / 60);
  const m = safeMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatDuration(minutes) {
  const mins = Number(minutes);
  if (!Number.isFinite(mins) || mins <= 0) return 'Unknown';
  const rounded = Math.round(mins);
  if (rounded < 60) return `${rounded} min`;

  const days = Math.floor(rounded / 1440);
  const remainingAfterDays = rounded % 1440;
  const hours = Math.floor(remainingAfterDays / 60);
  const m = remainingAfterDays % 60;

  if (days > 0) {
    if (hours > 0 && m > 0) return `${days}d ${hours}h ${m}m`;
    if (hours > 0) return `${days}d ${hours}h`;
    return m > 0 ? `${days}d ${m}m` : `${days}d`;
  }

  return m ? `${hours}h ${m}m` : `${hours}h`;
}

export function addMinutes(date, minutes) {
  return new Date(date.getTime() + Number(minutes || 0) * 60000);
}

export function parseDateTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return null;

  const normalizedTime = normalizeClockValue(timeValue) || timeValue;
  const parsed = new Date(`${dateValue}T${normalizedTime}`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function parseDateOnly(dateValue) {
  if (!dateValue) return null;
  const parsed = new Date(`${dateValue}T00:00:00`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function getBookingStartDate(booking) {
  return parseDateTime(booking.date, booking.time);
}

export function getExplicitBookingEndDate(booking) {
  if (!booking?.end_date) return null;

  const start = getBookingStartDate(booking);
  const fallbackClock = normalizeClockValue(booking.time) || '23:59';
  const explicitClock = normalizeClockValue(booking.end_time) || fallbackClock;
  const explicitEnd = parseDateTime(booking.end_date, explicitClock);

  if (!explicitEnd) return null;
  if (!start) return explicitEnd;
  return explicitEnd > start ? explicitEnd : null;
}

export function getBookingEndDate(booking) {
  const explicitEnd = getExplicitBookingEndDate(booking);
  if (explicitEnd) {
    const start = getBookingStartDate(booking);
    if (!start || explicitEnd > start) return explicitEnd;
  }

  const start = getBookingStartDate(booking);
  const durationMinutes = Number(booking.duration);
  const durationEnd = start && Number.isFinite(durationMinutes) && durationMinutes > 0
    ? addMinutes(start, durationMinutes)
    : null;

  if (durationEnd) return durationEnd;
  if (!start) return explicitEnd || null;

  return addMinutes(start, Number(booking.duration) || 60);
}

export function addOneHour(dateString, timeString) {
  const start = parseDateTime(dateString, timeString);
  if (!start) return '';
  return formatTimeFromMinutes((start.getHours() * 60 + start.getMinutes() + 60) % (24 * 60));
}

export function formatBookingRange(booking) {
  const start = getBookingStartDate(booking);
  const end = getBookingEndDate(booking);

  if (!start || !end) {
    return `${booking.date || 'Unknown date'} ${booking.time || ''}`.trim();
  }

  const sameDay = formatLocalDate(start) === formatLocalDate(end);
  if (sameDay) {
    return `${formatLocalDate(start)} ${formatLocalTime(start)}–${formatLocalTime(end)}`;
  }

  return `${formatDateTimeLabel(start)} → ${formatDateTimeLabel(end)}`;
}

export function normalizeAirfieldCode(value) {
  return String(value || '').toUpperCase();
}

// True only when an update's returned rows actually include the booking that was targeted,
// so a zero-row/RLS-denied/wrong-row result can never be mistaken for a successful update.
export function bookingUpdateContainsId(rows, bookingId) {
  const targetId = String(bookingId ?? '').trim();
  if (!targetId) return false;

  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
  return list.some((row) => String(row?.id ?? '') === targetId);
}
